export const UPCOMING_MAINTENANCE_URL =
	"https://status.pixieset.com/api/v2/scheduled-maintenances/upcoming.json"
export const STATUSPAGE_URL = "https://status.pixieset.com?utm_source=embed"
export const UPCOMING_MAINTENANCE_DISMISSED_IDS_KEY =
	"photo-editor:dismissed-scheduled-maintenance-ids"

const FALLBACK_TIME_ZONE = "America/Los_Angeles"

export interface UpcomingMaintenance {
	id: string
	name: string
	scheduledFor: string
	scheduledUntil: string
	timeZone: string
}

export interface UpcomingMaintenanceResult {
	maintenance: UpcomingMaintenance | null
	maintenanceIds: string[]
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

const nonEmptyString = (value: unknown): string | null => {
	if (typeof value !== "string") {
		return null
	}

	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : null
}

const isValidDate = (value: string): boolean => {
	return Number.isFinite(Date.parse(value))
}

const resolveTimeZone = (value: unknown): string => {
	const timeZone = nonEmptyString(value)
	if (!timeZone) {
		return FALLBACK_TIME_ZONE
	}

	try {
		new Intl.DateTimeFormat("en-US", { timeZone }).format()
		return timeZone
	} catch {
		return FALLBACK_TIME_ZONE
	}
}

export const parseUpcomingMaintenanceResponse = (
	value: unknown,
): UpcomingMaintenanceResult | null => {
	if (!isRecord(value) || !Array.isArray(value.scheduled_maintenances)) {
		return null
	}

	const timeZone = resolveTimeZone(isRecord(value.page) ? value.page.time_zone : undefined)
	const maintenances: UpcomingMaintenance[] = []

	for (const candidate of value.scheduled_maintenances) {
		if (!isRecord(candidate)) {
			continue
		}

		const id = nonEmptyString(candidate.id)
		const name = nonEmptyString(candidate.name)
		const scheduledFor = nonEmptyString(candidate.scheduled_for)
		const scheduledUntil = nonEmptyString(candidate.scheduled_until)

		if (
			!id ||
			!name ||
			!scheduledFor ||
			!scheduledUntil ||
			!isValidDate(scheduledFor) ||
			!isValidDate(scheduledUntil)
		) {
			continue
		}

		maintenances.push({
			id,
			name,
			scheduledFor,
			scheduledUntil,
			timeZone,
		})
	}

	maintenances.sort(
		(first, second) => Date.parse(first.scheduledFor) - Date.parse(second.scheduledFor),
	)

	return {
		maintenance: maintenances[0] ?? null,
		maintenanceIds: maintenances.map(({ id }) => id),
	}
}

export const fetchUpcomingMaintenance = async (
	signal?: AbortSignal,
): Promise<UpcomingMaintenanceResult | null> => {
	try {
		const response = await fetch(UPCOMING_MAINTENANCE_URL, {
			cache: "no-store",
			headers: { Accept: "application/json" },
			signal,
		})

		if (!response.ok) {
			return null
		}

		return parseUpcomingMaintenanceResponse(await response.json())
	} catch {
		return null
	}
}

const getTimeZoneName = (date: Date, timeZone: string): string => {
	const timeZoneName = new Intl.DateTimeFormat("en-US", {
		hour: "2-digit",
		hourCycle: "h23",
		minute: "2-digit",
		timeZone,
		timeZoneName: "short",
	})
		.formatToParts(date)
		.find((part) => part.type === "timeZoneName")?.value

	return timeZoneName ?? ""
}

export const formatMaintenanceWindow = (maintenance: UpcomingMaintenance): string => {
	const start = new Date(maintenance.scheduledFor)
	const end = new Date(maintenance.scheduledUntil)
	const dateFormatter = new Intl.DateTimeFormat("en-GB", {
		day: "2-digit",
		month: "short",
		timeZone: maintenance.timeZone,
		year: "numeric",
	})
	const date = dateFormatter.format(start)
	const endDate = dateFormatter.format(end)
	const timeFormatter = new Intl.DateTimeFormat("en-GB", {
		hour: "2-digit",
		hourCycle: "h23",
		minute: "2-digit",
		timeZone: maintenance.timeZone,
	})
	const formattedStart = timeFormatter.format(start)
	const formattedEnd = `${date === endDate ? "" : `${endDate} `}${timeFormatter.format(end)}`
	const startTimeZoneName = getTimeZoneName(start, maintenance.timeZone)
	const endTimeZoneName = getTimeZoneName(end, maintenance.timeZone)

	if (startTimeZoneName && endTimeZoneName && startTimeZoneName !== endTimeZoneName) {
		return `${date} from ${formattedStart} ${startTimeZoneName} to ${formattedEnd} ${endTimeZoneName}`
	}

	const timeZoneName = startTimeZoneName || endTimeZoneName

	return `${date} from ${formattedStart} to ${formattedEnd}${
		timeZoneName ? ` ${timeZoneName}` : ""
	}`
}

const readDismissedMaintenanceIds = (): Set<string> => {
	try {
		const storedValue = window.localStorage.getItem(UPCOMING_MAINTENANCE_DISMISSED_IDS_KEY)
		if (!storedValue) {
			return new Set()
		}

		const parsedValue: unknown = JSON.parse(storedValue)
		if (!Array.isArray(parsedValue)) {
			return new Set()
		}

		return new Set(parsedValue.filter((value): value is string => typeof value === "string"))
	} catch {
		return new Set()
	}
}

const writeDismissedMaintenanceIds = (ids: Set<string>): void => {
	try {
		if (ids.size === 0) {
			window.localStorage.removeItem(UPCOMING_MAINTENANCE_DISMISSED_IDS_KEY)
			return
		}

		window.localStorage.setItem(
			UPCOMING_MAINTENANCE_DISMISSED_IDS_KEY,
			JSON.stringify(Array.from(ids)),
		)
	} catch {
		// Dismissal remains effective for the current component session.
	}
}

export const pruneDismissedMaintenanceIds = (maintenanceIds: string[]): Set<string> => {
	const currentIds = new Set(maintenanceIds)
	const dismissedIds = readDismissedMaintenanceIds()
	const prunedIds = new Set(Array.from(dismissedIds).filter((id) => currentIds.has(id)))

	writeDismissedMaintenanceIds(prunedIds)
	return prunedIds
}

export const dismissMaintenance = (maintenanceId: string): void => {
	const dismissedIds = readDismissedMaintenanceIds()
	dismissedIds.add(maintenanceId)
	writeDismissedMaintenanceIds(dismissedIds)
}
